import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Pill } from "@/components/perf";
import TdApplications from "@/components/TdApplications";
import TournamentCreateForm from "@/components/TournamentCreateForm";
import { Plus, ChevronRight, Trash2, Users, Pencil, X, ClipboardList, DollarSign, Phone, Mail, Lightbulb } from "lucide-react";

const FORMAT_LABELS = {
  single_elim: "Single Elim", double_elim: "Double Elim",
  round_robin: "Round Robin", pool_play: "Pool Play",
};

export default function TournamentHub() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tourneysAll, setTourneysAll] = useState([]);
  const tourneys = isAdmin ? tourneysAll : tourneysAll.filter((t) => t.director_id === user?.id);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rosterFor, setRosterFor] = useState(null);
  const [reportFor, setReportFor] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/tournaments");
    setTourneysAll(data);
    try {
      const { data: sum } = await api.get("/tournaments/director/summary");
      setSummary(sum);
    } catch { setSummary(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}" and all its entries & matches?`)) return;
    try {
      await api.delete(`/tournaments/${t.id}`);
      toast.success("Tournament deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const counts = {
    open: tourneys.filter((t) => t.status === "open").length,
    live: tourneys.filter((t) => t.status === "in_progress").length,
    done: tourneys.filter((t) => t.status === "complete").length,
  };
  return (
    <div data-testid="tournament-hub">
      <p className="text-sm text-zinc-500 mb-4">{isAdmin
        ? "Full tournament ecosystem — approve directors, create any event from a local shootout to a pro-level tournament, open brackets, and enter or change scores live."
        : "Your tournaments — create any event from a local shootout to a pro-level tournament, open brackets, and enter or change scores live."}</p>

      <div className="grid grid-cols-3 gap-3 mb-3">
        {[["Open for Registration", counts.open, "#A3E635"], ["Live Brackets", counts.live, "#22D3EE"], ["Completed", counts.done, "#71717A"]].map(([l, v, c]) => (
          <div key={l} className="bg-pb-elevated border border-pb-border p-3 text-center">
            <div className="font-display font-black text-2xl" style={{ color: c }}>{v}</div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{l}</div>
          </div>
        ))}
      </div>

      {summary && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 bg-pb-elevated/50 border border-pb-border px-4 py-3 mb-5" data-testid="td-revenue-strip">
          <span className="flex items-center gap-1.5 text-sm">
            <DollarSign className="w-4 h-4 text-pb-red" />
            <span className="text-zinc-500 text-xs uppercase tracking-widest">Entry Revenue</span>
            <span className="font-display font-black text-lg text-pb-red" data-testid="td-total-revenue">${summary.total_revenue.toLocaleString()}</span>
          </span>
          <span className="text-xs text-zinc-500">{summary.total_paid_entries} paid entries across {summary.events.length} event{summary.events.length === 1 ? "" : "s"}</span>
        </div>
      )}

      <button data-testid="hub-create-tournament-btn" onClick={() => setShowCreate(!showCreate)}
        className="flex items-center gap-2 bg-pb-red text-black font-bold uppercase tracking-widest text-xs px-4 py-2.5 hover:bg-[#BEF264] active:scale-95 transition-all mb-4">
        <Plus className="w-4 h-4" /> {showCreate ? "Hide Form" : "Create Tournament"}
      </button>

      {showCreate && (
        <div className="border border-pb-red/40 bg-pb-elevated/40 p-4 mb-5 animate-fade-up" data-testid="hub-create-form">
          <TournamentCreateForm onCreated={(data) => { setShowCreate(false); navigate(`/tournaments/${data.id}`); }} />
        </div>
      )}

      <div className="bg-pb-elevated/30 border border-pb-border divide-y divide-pb-border/50 max-h-[420px] overflow-y-auto" data-testid="hub-tournament-list">
        {tourneys.length === 0 && <div className="p-5 text-sm text-zinc-600">No tournaments yet — create the first one above.</div>}
        {tourneys.map((t) => (
          <div key={t.id} data-testid={`hub-tournament-${t.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="flex-1 min-w-[200px]">
              <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                {t.name}
                <Pill color={t.status === "complete" ? "#71717A" : t.status === "in_progress" ? "#22D3EE" : "#A3E635"}>
                  {t.status === "in_progress" ? "Live" : t.status === "complete" ? "Final" : "Open"}
                </Pill>
              </div>
              <div className="text-xs text-zinc-500 flex items-center gap-3 mt-0.5 flex-wrap">
                <span>{FORMAT_LABELS[t.format]} · {(t.event_types || []).join(", ")}</span>
                <span>{t.date}{t.end_date ? ` – ${t.end_date}` : ""} · {t.location}</span>
                <span className="flex items-center gap-1"><Users className="w-3 h-3" />{t.entry_count}/{t.capacity}</span>
                <span>{t.entry_fee > 0 ? `$${t.entry_fee}` : "Free"}</span>
                <span className="text-zinc-600">Dir: {t.director_name}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button data-testid={`hub-manage-${t.id}`} onClick={() => navigate(`/tournaments/${t.id}`)}
                className="flex items-center gap-1 bg-pb-red text-black font-bold uppercase tracking-widest text-[10px] px-3 py-2 hover:bg-[#BEF264] active:scale-95 transition-all">
                Manage Bracket & Scores <ChevronRight className="w-3 h-3" />
              </button>
              <button data-testid={`hub-report-${t.id}`} onClick={() => setReportFor(t)} title="TD Setup Report — tips & timing for this event"
                className="w-8 h-8 flex items-center justify-center border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 transition-colors">
                <Lightbulb className="w-3.5 h-3.5" />
              </button>
              <button data-testid={`hub-roster-${t.id}`} onClick={() => setRosterFor(t)} title="Registered players & revenue"
                className="w-8 h-8 flex items-center justify-center border border-pb-border hover:border-pb-red hover:text-pb-red transition-colors">
                <ClipboardList className="w-3.5 h-3.5" />
              </button>
              <button data-testid={`hub-edit-${t.id}`} onClick={() => setEditing(t)} title="Edit tournament info"
                className="w-8 h-8 flex items-center justify-center border border-pb-border hover:border-pb-cyan hover:text-pb-cyan transition-colors">
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button data-testid={`hub-delete-${t.id}`} onClick={() => remove(t)}
                className="w-8 h-8 flex items-center justify-center border border-pb-border hover:border-red-500 hover:text-red-400 transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-pb-surface border border-pb-cyan w-full max-w-3xl my-8 animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="edit-tournament-modal">
            <div className="flex items-center justify-between p-5 border-b border-pb-border sticky top-0 bg-pb-surface z-10">
              <div className="flex items-center gap-2">
                <Pencil className="w-5 h-5 text-pb-cyan" />
                <h3 className="font-display font-black text-lg uppercase">Edit — {editing.name}</h3>
              </div>
              <button data-testid="edit-tournament-close" onClick={() => setEditing(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              <TournamentCreateForm initial={editing} onCreated={() => { setEditing(null); load(); }} />
            </div>
          </div>
        </div>
      )}

      {isAdmin && <TdApplications />}

      {rosterFor && <RosterModal tourney={rosterFor} onClose={() => setRosterFor(null)} />}
      {reportFor && <SetupReportModal tourney={reportFor} onClose={() => setReportFor(null)} />}
    </div>
  );
}

function RosterModal({ tourney, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/tournaments/${tourney.id}/director/roster`).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [tourney.id]);

  return (
    <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-pb-surface border border-pb-red w-full max-w-4xl my-8 animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="roster-modal">
        <div className="flex items-center justify-between p-5 border-b border-pb-border sticky top-0 bg-pb-surface z-10">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-pb-red" />
            <h3 className="font-display font-black text-lg uppercase">{tourney.name} — Roster & Revenue</h3>
          </div>
          <button data-testid="roster-modal-close" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {data === null ? (
          <div className="p-6 text-sm text-zinc-500 animate-pulse">Loading roster…</div>
        ) : data === false ? (
          <div className="p-6 text-sm text-red-400">Could not load roster.</div>
        ) : (
          <div className="p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {[["Collected", `$${data.revenue.collected.toLocaleString()}`, "#A3E635", "roster-rev-collected"],
                ["Paid Entries", data.revenue.paid_entries, "#A3E635", "roster-rev-paid"],
                ["Free / Unpaid", data.revenue.free_entries, "#71717A", "roster-rev-free"],
                ["Total Entries", data.revenue.total_entries, "#22D3EE", "roster-rev-total"]].map(([l, v, c, tid]) => (
                <div key={l} className="bg-pb-elevated border border-pb-border p-3 text-center" data-testid={tid}>
                  <div className="font-display font-black text-xl" style={{ color: c }}>{v}</div>
                  <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{l}</div>
                </div>
              ))}
            </div>
            {data.roster.length === 0 ? (
              <div className="text-sm text-zinc-600 border border-pb-border/60 p-5" data-testid="roster-empty">No registrations yet.</div>
            ) : (
              <div className="overflow-x-auto border border-pb-border/60 max-h-[420px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-pb-surface">
                    <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-pb-border/60">
                      <th className="text-left px-4 py-2.5">#</th>
                      <th className="text-left px-4 py-2.5">Player / Team</th>
                      <th className="text-left px-4 py-2.5">Event</th>
                      <th className="text-left px-4 py-2.5">Division</th>
                      <th className="text-left px-4 py-2.5">Paid</th>
                      <th className="text-left px-4 py-2.5">Contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.roster.map((e, i) => (
                      <tr key={e.id} className="border-b border-pb-border/40 hover:bg-pb-elevated/50" data-testid={`roster-row-${e.id}`}>
                        <td className="px-4 py-2.5 text-zinc-600 font-mono text-xs">{i + 1}</td>
                        <td className="px-4 py-2.5 font-semibold whitespace-nowrap">{e.display_name}</td>
                        <td className="px-4 py-2.5 text-zinc-400 text-xs">{e.event_type || "—"}{e.age_bracket ? ` · ${e.age_bracket}` : ""}</td>
                        <td className="px-4 py-2.5 text-zinc-400 text-xs">{e.division || "—"}</td>
                        <td className="px-4 py-2.5">
                          <Pill color={e.paid ? "#A3E635" : "#71717A"}>{e.paid ? "Paid" : "Free"}</Pill>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-zinc-400">
                          <div className="flex flex-col gap-0.5">
                            {e.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-zinc-600" />{e.phone}</span>}
                            {e.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3 text-zinc-600" />{e.email}</span>}
                            {!e.phone && !e.email && "—"}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function SetupReportModal({ tourney, onClose }) {
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState(null);
  const [published, setPublished] = useState(Boolean(tourney.published_schedule?.length || tourney.schedule_published_at));
  const publishSchedule = async () => {
    try {
      await api.post(`/tournaments/${tourney.id}/publish-schedule`);
      setPublished(true);
      toast.success("Schedule published — players can now see it on the tournament card");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const unpublishSchedule = async () => {
    try {
      await api.delete(`/tournaments/${tourney.id}/publish-schedule`);
      setPublished(false);
      toast.success("Schedule removed from the tournament card");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const evTypes = tourney.event_types || [];
  const [runOrder, setRunOrder] = useState(tourney.run_order || {});
  const [savingOrder, setSavingOrder] = useState(false);
  const saveRunOrder = async (slots) => {
    setSavingOrder(true);
    try {
      const { data } = await api.post(`/tournaments/${tourney.id}/run-order`, { slots });
      setRunOrder(data.run_order || {});
      toast.success(Object.keys(data.run_order || {}).length ? "Run order saved — schedule rebuilt" : "Run order cleared — back to gendered-first default");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSavingOrder(false);
  };
  const load = useCallback(() => {
    setRep(null); setErr(null);
    api.get(`/tournaments/${tourney.id}/setup-report`)
      .then(({ data }) => setRep(data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail)));
  }, [tourney.id]);
  useEffect(() => { load(); }, [load]);
  const TIP_STYLE = {
    good: "border-green-500/40 bg-green-500/5 text-green-300",
    warn: "border-amber-500/40 bg-amber-500/5 text-amber-300",
    info: "border-pb-cyan/30 bg-pb-cyan/5 text-zinc-300",
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-pb-surface border border-amber-500/50 w-full max-w-2xl p-5 my-8 animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="setup-report-modal">
        <div className="flex items-center justify-between mb-1">
          <span className="flex items-center gap-2 font-display font-black uppercase text-amber-400"><Lightbulb className="w-4 h-4" /> TD Setup Report</span>
          <div className="flex items-center gap-2">
            <button data-testid="setup-report-refresh" onClick={load} className="text-[10px] uppercase tracking-widest font-bold text-pb-cyan border border-pb-cyan/40 px-2.5 py-1 hover:bg-pb-cyan/10 transition-colors">Re-generate</button>
            <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="text-xs text-zinc-500 mb-4">{tourney.name} — live numbers as of right now; re-generate any time as registrations come in.</div>
        {err && <div className="text-sm text-red-400">{err}</div>}
        {!rep && !err && <div className="text-sm text-zinc-500 animate-pulse">Crunching your setup…</div>}
        {rep && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              {[["Teams", rep.overall.total_teams], ["Courts", rep.overall.courts],
                ["Est. games", rep.overall.total_games_est],
                ["Est. length", rep.overall.hours_range ? `${rep.overall.hours_range[0]}–${rep.overall.hours_range[1]} hrs` : "—"]].map(([l, v]) => (
                <div key={l} className="border border-pb-border bg-pb-elevated/40 p-2.5">
                  <div className="font-display font-black text-lg text-white">{v}</div>
                  <div className="text-[9px] uppercase tracking-widest text-zinc-500">{l}</div>
                </div>
              ))}
            </div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-600">Timing uses the 12–15 minute-per-game rule · playoff format: QF {rep.playoff_formats.quarters === 1 ? "1 game" : `Bo${rep.playoff_formats.quarters}`} / SF {rep.playoff_formats.semis === 1 ? "1 game" : `Bo${rep.playoff_formats.semis}`} / F {rep.playoff_formats.finals === 1 ? "1 game" : `Bo${rep.playoff_formats.finals}`}</div>
            {rep.overall.verdict && (
              <div className={`border px-3 py-2 text-xs font-bold ${rep.overall.verdict.startsWith("Your current") ? "border-green-500/40 bg-green-500/5 text-green-300" : "border-amber-500/40 bg-amber-500/5 text-amber-300"}`} data-testid="report-verdict">
                {rep.overall.verdict}
              </div>
            )}
            {rep.divisions.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-1.5">Exact Breakdown — Event · Division · Age</div>
                <div className="space-y-1.5">
                  {rep.divisions.map((d) => (
                    <div key={d.label} className="border border-pb-border/60 px-3 py-2 text-xs" data-testid={`report-division-${d.label}`}>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="font-bold text-zinc-200">{d.label}</span>
                        <span className="text-zinc-500">{d.teams} teams{d.solos ? ` (${d.solos} solo)` : ""}</span>
                        {d.pools_suggested && <span className="text-pb-cyan">{d.pools_suggested} pools of {[...new Set(d.pool_sizes)].join("/")}</span>}
                        {d.rr_games !== undefined && <span className="text-zinc-500">{d.rr_games} RR games</span>}
                        {d.elim_games !== undefined && <span className="text-zinc-500">{d.elim_games} bracket games</span>}
                        {d.playoff_games_est !== undefined && <span className="text-zinc-500">+{d.playoff_games_est} playoff</span>}
                        {d.minutes_range && <span className="ml-auto text-amber-300">{Math.round(d.minutes_range[0])}–{Math.round(d.minutes_range[1])} min</span>}
                      </div>
                      {d.recommendation && (
                        <div className={`mt-1 text-[11px] leading-relaxed ${d.recommendation.startsWith("CHANGE") ? "text-amber-300" : "text-zinc-400"}`}>{d.recommendation}</div>
                      )}
                      {d.fixes?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {d.fixes.map((f) => (
                            <button key={f.label} data-testid={`report-fix-${f.action}-${f.value}`}
                              title="Applies to the whole tournament setup"
                              onClick={async () => {
                                try {
                                  await api.post(`/tournaments/${tourney.id}/setup-fix`, { action: f.action, value: String(f.value) });
                                  toast.success(`Applied — ${f.label}`);
                                  load();
                                } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                              }}
                              className="text-[10px] uppercase tracking-widest font-bold border border-green-500/50 text-green-300 px-2.5 py-1 hover:bg-green-500/10 transition-colors">
                              ⚡ {f.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {rep.schedule?.length > 0 && (
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold">Suggested Schedule — start times{Object.keys(runOrder).length ? " · your run order" : " · gendered before Mixed"}</div>
                  <div className="flex items-center gap-2">
                    <button data-testid="publish-schedule-btn" onClick={publishSchedule}
                      className="text-[10px] uppercase tracking-widest font-bold border border-green-500/50 text-green-300 px-2.5 py-1 hover:bg-green-500/10 transition-colors">
                      {published ? "↻ Re-publish to Card" : "📌 Publish to Card"}
                    </button>
                    {published && (
                      <button data-testid="unpublish-schedule-btn" onClick={unpublishSchedule}
                        className="text-[10px] uppercase tracking-widest font-bold border border-zinc-700 text-zinc-500 px-2.5 py-1 hover:text-red-400 hover:border-red-500/40 transition-colors">
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                {published && <div className="text-[10px] text-green-400/80 mb-1.5" data-testid="schedule-published-note">✓ Live on the tournament card — players see this day-by-day schedule. Re-publish after registrations shift.</div>}
                {evTypes.length > 1 && (
                  <div className="border border-pb-cyan/25 bg-pb-cyan/5 p-2.5 mb-2" data-testid="run-order-editor">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-pb-cyan font-bold">Run Order — pick the slot each event plays in</span>
                      {Object.keys(runOrder).length > 0 && (
                        <button data-testid="run-order-clear" disabled={savingOrder} onClick={() => saveRunOrder({})}
                          className="text-[9px] uppercase tracking-widest font-bold text-zinc-500 border border-zinc-700 px-2 py-0.5 hover:text-red-400 hover:border-red-500/40 transition-colors">Reset to default</button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {evTypes.map((ev) => (
                        <label key={ev} className="flex items-center gap-1.5 text-[11px] text-zinc-300">
                          {ev}
                          <select data-testid={`run-order-${ev.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            value={runOrder[ev] || ""} disabled={savingOrder}
                            onChange={(e) => {
                              const next = { ...runOrder };
                              if (e.target.value) next[ev] = Number(e.target.value); else delete next[ev];
                              saveRunOrder(next);
                            }}
                            className="bg-pb-elevated border border-pb-border text-white text-[11px] px-1.5 py-1 focus:border-pb-cyan outline-none">
                            <option value="">—</option>
                            {evTypes.map((_, i) => <option key={i + 1} value={i + 1}>{i + 1}{["st", "nd", "rd"][i] || "th"}</option>)}
                          </select>
                        </label>
                      ))}
                    </div>
                    <div className="text-[9px] text-zinc-500 mt-1.5 leading-relaxed">Same slot number = those events run at the same time (courts split between them). Slots play in order. Leave blank to use the default gendered-first order. Re-publish to push changes to the card.</div>
                  </div>
                )}
                <div className="space-y-1" data-testid="report-schedule">
                  {[...new Set(rep.schedule.map((s) => s.day || 1))].map((day) => (
                    <div key={day}>
                      {rep.schedule.some((s) => (s.day || 1) > 1) && (
                        <div className="text-[9px] uppercase tracking-[0.25em] text-yellow-400 font-bold mt-1.5 mb-1" data-testid={`schedule-day-${day}`}>Day {day}</div>
                      )}
                      {rep.schedule.filter((s) => (s.day || 1) === day).map((s, i) => (
                        <div key={`${s.label}-${i}`} className="flex items-center gap-3 border border-pb-border/60 px-3 py-1.5 text-xs mb-1">
                          <span className="font-mono text-pb-cyan shrink-0">{s.window}</span>
                          <span className="text-zinc-300 truncate">{s.label}</span>
                          {s.slot && <span className="text-[9px] uppercase tracking-widest text-pb-cyan/70 border border-pb-cyan/30 px-1.5 shrink-0">Slot {s.slot}{s.courts ? ` · ${s.courts} cts` : ""}</span>}
                          {s.phase === "Mixed" && <span className="text-[9px] uppercase tracking-widest text-amber-400 border border-amber-500/40 px-1.5 shrink-0">Mixed — after all gendered</span>}
                          <span className="ml-auto text-zinc-600 shrink-0">{s.games} games</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {rep.run_plan?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-1.5">How To Run It — Step By Step</div>
                <ol className="space-y-1.5" data-testid="report-run-plan">
                  {rep.run_plan.map((step, i) => (
                    <li key={i} className="flex gap-2.5 text-xs text-zinc-300 leading-relaxed">
                      <span className="shrink-0 w-5 h-5 flex items-center justify-center border border-pb-cyan/40 text-pb-cyan font-bold text-[10px]">{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-1.5">Tips & Suggestions</div>
              <div className="space-y-1.5" data-testid="report-tips">
                {rep.tips.map((tip, i) => (
                  <div key={i} className={`border px-3 py-2 text-xs leading-relaxed ${TIP_STYLE[tip.level] || TIP_STYLE.info}`}>{tip.text}</div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

